// Empty trash (Daniil's call, 2026-10-22; spec S1-library): every file of the brand's trash
// deleted for good, its bytes freed from the team's storage at once (`POST .../assets/trash/empty`).
// One click and the question, which names the count and the size; then a line says what it
// freed (web/AGENTS.md: no toasts). Shown to an admin or the owner, who may delete anyone's file;
// the API decides.
import { Button, Group, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { useEmptyTrash, useGetTrash } from "../../api/generated/endpoints/assets/assets";
import type { TrashEmptied } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { countOf, formatBytes } from "../../ui/format";

export function EmptyTrash({ teamId, brandId }: { teamId: string; brandId: string }) {
  const queryClient = useQueryClient();
  const summary = useGetTrash(teamId, brandId).data;
  const [asking, setAsking] = useState(false);
  const [freed, setFreed] = useState<TrashEmptied | null>(null);
  const empty = useEmptyTrash({
    mutation: {
      onSuccess: async (done) => {
        setAsking(false);
        setFreed(done);
        await refreshLibraryLists(queryClient, teamId, brandId);
      },
    },
  });
  const files = summary?.files ?? 0;
  return (
    <>
      <Group justify="space-between" gap="sm">
        {freed === null ? (
          <span />
        ) : (
          <Text component="output" size="sm">
            Emptied the trash: {countOf(freed.purged, "file")} deleted, {formatBytes(freed.bytes_freed)} freed.
          </Text>
        )}
        <Button
          variant="outline"
          color="red"
          leftSection={<Trash2 size={18} aria-hidden />}
          disabled={files === 0}
          onClick={() => {
            empty.reset();
            setFreed(null);
            setAsking(true);
          }}
        >
          Empty trash
        </Button>
      </Group>
      <ConfirmModal
        opened={asking}
        title="Empty trash?"
        confirmLabel="Empty trash"
        onConfirm={() => empty.mutate({ teamId, brandId })}
        onCancel={() => setAsking(false)}
        pending={empty.isPending}
        error={empty.isError ? <ErrorAlert error={empty.error} /> : null}
      >
        <Text size="sm">
          Permanently delete {countOf(files, "file")} ({formatBytes(summary?.bytes ?? 0)})? This can't be undone.
        </Text>
      </ConfirmModal>
    </>
  );
}
